import * as _ from 'lodash';
import { PropTypes } from 'prop-types';
import React, { PureComponent } from 'react';
import onClickOutside from 'react-onclickoutside';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Nav } from 'reactstrap';
import styled from 'styled-components';
import colors from '../res/styles/Colors';
import { randomId } from '../utils/Utils';

const EMPTY_VALUE = {
    label: '',
    key: randomId()
};

class SimpleInputDropdown extends PureComponent {
    static propTypes = {
        options: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                key: PropTypes.string.isRequired
            })
        ).isRequired,
        className: PropTypes.string,
        text: PropTypes.string,
        badgeCount: PropTypes.number,
        isInput: PropTypes.bool,
        isScrollable: PropTypes.bool,
        hasEmptyValue: PropTypes.bool,
        multiSelect: PropTypes.bool,
        selectedValues: PropTypes.arrayOf(PropTypes.string),
        keys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
        onChangeValue: PropTypes.func
    };

    static defaultProps = {
        isScrollable: false,
        hasEmptyValue: false,
        multiSelect: false,
        selectedValues: [],
        keys: [],
        badgeCount: 0
    };

    state = { dropdownOpen: false, freeText: '' };

    toggle = () => {
        this.setState({
            dropdownOpen: !this.state.dropdownOpen,
            freeText: ''
        });
    };

    handleDropdownItemClick = (index, selectedValue, event) => {
        // Close dropdown after selection for all cases (single and multi-select)
        if (this.props.onChangeValue == null) {
            return;
        }
        const selectedIndex = this.props.options.findIndex((v) => _.isEqual(v, selectedValue));
        this.props.onChangeValue(selectedIndex === -1 ? null : selectedIndex, selectedValue?.label);
    };

    isSelected = (label) => {
        return this.props.selectedValues?.includes(label);
    };

    handleClickOutside() {
        this.setState({ freeText: '' });
    }

    onChangeFreeText = (event) => {
        const value = event?.target?.value;
        if (value != null) {
            this.setState({ freeText: value });
        }
    };

    render() {
        const options = this.props.hasEmptyValue
            ? [EMPTY_VALUE, ...this.props.options]
            : this.props.options;
        const filteredOptions =
            this.state.freeText.length > 0
                ? options.filter((v) =>
                    v.label?.toLowerCase()?.includes(this.state.freeText?.toLowerCase())
                )
                : options;
        const { badgeCount } = this.props;
        return (
            <StyledNav className={['button-dropdown', this.props.className].join(' ')}>
                <Dropdown isOpen={this.state.dropdownOpen} toggle={this.toggle}>
                    <DropdownToggle nav caret>
                        {this.props.isInput && this.state.dropdownOpen ? (
                            <StyledInput autoFocus type='text' onChange={this.onChangeFreeText} />
                        ) : (
                            <>
                                <span className="dropdown-text">{this.props.text}</span>
                                {badgeCount > 0 && <span className="dropdown-badge">+{badgeCount}</span>}
                            </>
                        )}
                    </DropdownToggle>
                    {/* .toString() required by React */}
                    {filteredOptions && (
                        <StyledDropdownMenu scrollable={this.props.isScrollable.toString()}>
                            <DropdownInnerWrapper>
                                {filteredOptions.map((option, index) => (
                                    <StyledDropdownItem
                                        key={index}
                                        className={this.props.multiSelect && this.isSelected(option.label) ? 'selected' : ''}
                                        toggle={true}
                                        onClick={(e) => this.handleDropdownItemClick(index, option, e)}>
                                        {option.label}
                                    </StyledDropdownItem>
                                ))}
                            </DropdownInnerWrapper>
                        </StyledDropdownMenu>
                    )}
                </Dropdown>
            </StyledNav>
        );
    }
}

export default onClickOutside(SimpleInputDropdown);

const StyledInput = styled.input``;

const StyledNav = styled(Nav)`
    border-radius: 50px;

    .dropdown {
        width: 100%;
        text-align: center;
    }

    .dropdown-toggle {
        display: flex;
        align-items: center;
        width: 100%;
        white-space: nowrap;
        padding-right: 25px; /* Space for caret */
    }

    .dropdown-toggle .dropdown-text {
        flex: 1;
        min-width: 0; /* Allows text to shrink */
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .dropdown-toggle .dropdown-badge {
        flex-shrink: 0;
        margin-left: 4px;
        font-weight: 500;
    }

    .dropdown-toggle::after {
        position: absolute;
        right: 13px;
        top: 50%;
        margin-top: -3px;
    }

    .dropdown a,
    .nav-item a,
    .nav-link a {
        background-color: inherit;
        color: inherit;
        font-size: 12px;
    }

    .dropdown-item:hover {
        background-color: ${colors.light_red};
    }

    button:focus {
        outline: none;
    }

    .nav-link.active {
        background-color: ${colors.lighter_green};
        color: $green;
        border-radius: 50px;
    }

    p {
        font-size: 14px;
        margin: auto;
    }
`;

const StyledDropdownMenu = styled(DropdownMenu)`
    &&.dropdown-menu.show {
        width: 100%;
        border-radius: 5px;
        overflow: visible !important;
        padding: 0 !important;
        background-color: transparent !important;
        position: absolute !important;
        z-index: 1000;
        border: 1px solid rgba(0,0,0,.15);
    }
`;

const DropdownInnerWrapper = styled.div`
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    background-color: white;
    border-radius: 5px;
    
    /* Force white background on scrollbar area */
    scrollbar-color: ${colors.green} white;
    scrollbar-width: thin;
    
    /* Webkit browsers */
    &::-webkit-scrollbar {
        width: 8px;
        background-color: white;
    }
    
    &::-webkit-scrollbar-track {
        background-color: white;
    }
    
    &::-webkit-scrollbar-thumb {
        background-color: ${colors.green};
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background-color: ${colors.dark_green};
    }
`;

const StyledDropdownItem = styled(DropdownItem)`
    height: 30px;
    padding: 5px 15px;
    font-size: 14px;

    &.selected {
        background-color: ${colors.lighter_green};
        font-weight: 500;
    }
`;
